import {
  faFilter,
  faFolderOpen,
  faHandHoldingUsd,
  faSearch,
  faStream,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";

import {
  GSInput,
  GSItemList,
  GSListPage,
  GSEmptyList,
  GSActionBar,
  GSButton,
} from "golfstatus_react_components";
import { useState } from "react";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useMatch, useNavigate } from "react-router-dom";
import { Outlet } from "react-router-dom";
import {
  getEventOrderSummary,
  getOrders,
  selectLoading,
  selectOrderFilter,
  selectOrders,
  selectOrderSummary,
  setOrderFilter,
} from "../../../reducers/reportingSlice";
import TournamentReportingOrderItem from "./tournament-reporting-order-item";
import "./tournament-reporting.scss";
import TournamentReportingOrderSummary from "./tournament-reporting-order-summary";
import { selectCurrentTournament } from "../../../reducers/tournamentSlice";
import {
  getOrgBankAccount,
  selectOrganizationBankAccount,
} from "../../../reducers/organizationSlice";
import { getTournamentCustomerInvoice, getTournamentPaymentSettings } from "../../../reducers/settingsSlice";
import {selectLoggedInUser} from "../../../reducers/appSlice";
import {isOrganizationUser} from "../../../helpers/Access";

const TournamentReporting = (props) => {
  const [search, setSearch] = useState("");

  const match = useMatch("/:tournamentID/reporting/*");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentUser = useSelector(selectLoggedInUser);
  const tournamentOrders = useSelector(selectOrders);
  const tournamentEventOrderSummary = useSelector(selectOrderSummary);
  const currentTournament = useSelector(selectCurrentTournament);
  const orgBankAccount = useSelector(selectOrganizationBankAccount);
  const orderFilter = useSelector(selectOrderFilter);
  const loading = useSelector(selectLoading);

  useEffect(() => {
    if (match?.params?.tournamentID) {
      dispatch(getOrders(match.params.tournamentID));
      dispatch(getEventOrderSummary(match.params.tournamentID));
      dispatch(getTournamentPaymentSettings(match.params.tournamentID))?.then?.(
        (result) => {
          dispatch(getTournamentCustomerInvoice(result?.payload?.id));
        }
      );
    }
  }, [match?.params?.tournamentID, dispatch]);

  useEffect(() => {
    if (
      currentTournament?.organization?.id &&
      !orgBankAccount?.accountLastFour
    ) {
      dispatch(getOrgBankAccount(currentTournament?.organization?.id));
    }
  }, [
    currentTournament?.organization?.id,
    orgBankAccount?.accountLastFour,
    dispatch,
  ]);

  const getFilterButtons = () => {
    return orderFilter.map?.((filter) => {
      return {
        name: filter.name,
        removeAction: () => {
          dispatch(
            setOrderFilter(orderFilter.filter?.((f) => f.name !== filter.name))
          );
        },
      };
    });
  };

  const getPageActions = () => {
    if (isOrganizationUser(currentUser, currentTournament.organization)) {
      return [{
        id: 1,
        buttonTitle: "Payouts",
        type: "black mobile-icon",
        actionClick: () => {
          navigate("payouts");
        },
        actionIcon: faHandHoldingUsd,
      }, {
        id: 1,
        buttonTitle: "Documents",
        type: "light-grey mobile-icon",
        actionIcon: faFolderOpen,
        actionClick: () => {
          navigate("documents");
        },
      }];
    } else {
      return [];
    }
  };

  const getSearchActions = () => {
    return [
      {
        id: 1,
        type: "mobile-icon",
        actionIcon: faFilter,
      },
    ];
  };

  const getDefaultSearch = () => {
    return (
      <GSInput
        textValue={search}
        leftIcon={faSearch}
        rightIcon={faTimesCircle}
        rightIconClick={() => {
          setSearch("");
        }}
        onChange={(e) => {
          setSearch(e.target.value);
        }}
        placeholder="Search Orders..."
      ></GSInput>
    );
  };

  const getEmptyListMessage = () => {
    return (
      <GSEmptyList
        title="Manage Orders"
        detail="View orders, manage refunds, request payouts, and download reports."
      />
    );
  };

  const getEmptySearchMessage = () => {
    return (
      <GSEmptyList
        title="We couldn’t find the order you’re looking for…"
        detail="Try changing the search term and filter or contact GolfStatus support."
        actions={[
          {
            title: "Clear All",
            type: "light-grey",
            onClick: () => {
              setSearch("");
              dispatch(setOrderFilter([]));
            },
          },
        ]}
      />
    );
  };

  const getNoAccessListMessage = () => {
    return (
      <GSItemList
        type="vertical selectable orders"
        emptyMessage={
          <GSEmptyList
            title="You don't have access to Orders & Payouts"
            detail={`Orders, payouts, and reporting data is only available to ${currentTournament?.organization?.name}'s organization members and tournament organizers.`}
          />
        }
      />
    );
  };

  const isSidePanelOpen = () => {
    return match?.params?.["*"] !== "";
  };

  const getRouter = () => {
    return <Outlet />;
  };

  const itemSelected = (item) => {
    if (!isSidePanelOpen()) {
      navigate(item.id);
    } else {
      navigate(-1);
    }
  };

  const filteredOrders = () => {
    let filteredItems = tournamentOrders?.filter?.((to) => {
      let searchTokens = [];
      // allow search by registration order key
      searchTokens.push(to.id);
      // allow search by registrant info
      searchTokens.push(to.registrationCustomer?.fullName?.toLowerCase());
      searchTokens.push(to.registrationCustomer?.email?.toLowerCase());
      // allow search by package names
      for (const [, value] of Object.entries(to.registrationOrderItems)) {
        if (value.tournamentPackage) {
          searchTokens.push(value.tournamentPackage?.name?.toLowerCase?.());
        }
      }
      // allow search by purchase cost
      if (to.purchaseCost) {
        searchTokens.push(`$${parseFloat(to?.purchaseCost)?.toFixed(2)}`);
      }
      // allow search by last4
      if (to.last4) {
        searchTokens.push(to.last4);
      }
      // if customer invoice, allow search by customer invoice id
      if (to.paymentType === 'customer_invoice') {
        searchTokens.push(to.customerInvoiceId);
      }
      // allow search by Stripe order
      if (to.stripeOrder) {
        searchTokens.push(to?.stripeOrder?.toLowerCase?.())
      }

      return searchTokens.join(" ").includes?.(search?.toLowerCase?.());
    });

    orderFilter.forEach?.((filter) => {
      filteredItems = filteredItems.filter((item) => filter.filter(item));
    });

    return filteredItems;
  };

  const searchActions = () => {
    return [
      {
        actionIcon: faStream,
        actionClick: () => {
          navigate("filter");
        },
      },
    ];
  };

  const getItemList = () => {
    const filterButtons = getFilterButtons();
    return (
      <div className="content">
        <div className="hero">
          <TournamentReportingOrderSummary
            {...tournamentEventOrderSummary}
            showRequest={
              currentTournament.state === "final" &&
              (currentTournament?.organization?.stripeId ||
                orgBankAccount?.status === "verified") &&
              tournamentEventOrderSummary?.availableFunds > 0
            }
          />
        </div>
        <GSActionBar
          type="reporting-header"
          pageActions={searchActions()}
          header={<div className="reporting-search">{getDefaultSearch()}</div>}
        />
        {filterButtons?.length > 0 && (
          <div className="filter-buttons">
            <GSItemList
              itemSelected={(button) => button.removeAction()}
              type="horizontal medium-gap"
              items={filterButtons}
              listItem={(button) => (
                <GSButton
                  title={button.name}
                  type="black pill secondary"
                  rightIcon={faTimesCircle}
                ></GSButton>
              )}
            ></GSItemList>
          </div>
        )}
        <GSItemList
          type="vertical selectable orders"
          items={filteredOrders()}
          listItem={(item) => <TournamentReportingOrderItem {...item} />}
          emptyMessage={
            search !== "" || orderFilter.length > 0
              ? getEmptySearchMessage()
              : getEmptyListMessage()
          }
          itemSelected={itemSelected}
          loading={loading.length > 0}
          loadingMainText="Loading Orders..."
        ></GSItemList>
      </div>
    );
  };

  return (
    <tournament-reporting
      onClick={(e) => {
        if (isSidePanelOpen()) {
          navigate(-1);
        }
      }}
    >
      <GSListPage
        title="Orders & Payouts"
        getPageActions={getPageActions}
        getSearchActions={getSearchActions}
        getItemList={isOrganizationUser(currentUser, currentTournament.organization) ? getItemList : getNoAccessListMessage}
        isSidePanelOpen={isSidePanelOpen}
        getRouter={getRouter}
      ></GSListPage>
    </tournament-reporting>
  );
};

export default TournamentReporting;
